import * as S from '../styles/RefundsSignatureStyles';
import * as Lay from '../styles/RefundsStyles';
import { useEffect, useState } from 'react';
import { useHistory, RouteComponentProps, useLocation } from 'react-router-dom';
import moment from "moment";
import SignaturesService, { ISignature } from '@/services/SignaturesService';
import { SignatureComponent } from '@/components/Signature/SignatureComponent';
import OrderService, { OrderType } from '@/services/OrderService';
import ProcessingModalComponent from "../../../components/Modals/ProcessingModalComponent";
import { useBoolean } from "@fluentui/react-hooks";
import { MessageBar, MessageBarType, Spinner, SpinnerSize } from "@fluentui/react";
import DocumentTitle from 'react-document-title';
import React from 'react';
import { currencyNeutreFormatter } from '@/utils/numberFormatter';

interface SignatureProps extends RouteComponentProps {}

const RefundsSignature = (props: SignatureProps) =>
{
    const { state } = useLocation<any>();
    let refund: any = state?.refund;
    let defaultSignature: any = state?.signature;
    const history = useHistory();
    const [phoneNumber, setPhoneNumber] = useState("");
    const [signature, setSignature] = useState(defaultSignature);
    const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
    const [showAlert, { toggle: toggleShowAlert }] = useBoolean(false);
    const isOutdated = state?.outdated === true;
    const [proceedToSignature, setProceedToSignature] = useState(!isOutdated);
    const [sendingNewCode, setSendingNewCode] = useState(false);
    const [restartCountdownTrigger, setRestartCountdownTrigger] = useState(0);
    const outdatedPlanId = state?.outdatedPlanId;
    const endDate = state?.endDate;
    const daysExpired = state?.daysExpired;
    const recharge = state?.recharge;
    const refundAmount = state?.refundAmount;

    const processExpirationDate = (signature: ISignature) =>
    {
        if(signature && typeof signature.expiresAt === "string")
        {
            signature.expiresAt = moment(signature.expiresAt).toDate();
        }
    }
    processExpirationDate(defaultSignature);
    useEffect(() =>
    {
        window.scrollTo(0, 0);
    }, []);
    useEffect(() =>
    {
        var operationPhone = state?.refund?.userIdentity?.operationPhone;
        if(operationPhone)
        {
            setPhoneNumber("X".repeat(operationPhone.length - 3) + operationPhone.slice(operationPhone.length - 3, operationPhone.length));
        }
    }, [state]);
    if(!state?.signature || !state?.refund)
    {
        history.push("/portfolio");
        return null;
    }

    const getFormStateForBack = () =>
    {
        if(state?.cancelledFormState)
        {
            return state.cancelledFormState;
        }
        const v = refund?.values;
        if(!v)
        {
            return undefined;
        }
        const isin = v.fundIsin ?? v.FundIsin;
        const name = v.fundName ?? v.FundName;
        const finametrixId = v.fund ?? v.Fund;
        if(!isin)
        {
            return undefined;
        }
        return {
            origin: { isin, name: name ?? "", finametrixId },
            amountType: v.amountType ?? v.AmountType,
            amount: v.amount ?? v.Amount,
            refundIBAN: v.iban ?? v.Iban
        };
    };
    const goBackToRefunds = () =>
    {
        history.push("/refunds", getFormStateForBack() ?? undefined);
    };

    const handleProcessRefund = () =>
    {
        if(!signature?.id)
        {
            return;
        }
        setSendingNewCode(true);
        SignaturesService.getRefreshSignature({signatureIds: [signature.id]}).then((response: any) =>
        {
            setSendingNewCode(false);
            if(!response?.data?.signatures || response.data.signatures.length === 0)
            {
                toggleShowAlert();
                return;
            }
            const newSignature = response.data.signatures[0];
            processExpirationDate(newSignature);
            setSignature(newSignature);
            setRestartCountdownTrigger(Date.now());
            setProceedToSignature(true);
        })
        .catch((error: any) =>
        {
            setSendingNewCode(false);
            const signatures = error?.response?.data?.signatures;
            if(signatures && Array.isArray(signatures) && signatures.length > 0)
            {
                const errCode = error?.response?.data?.errorCode;
                if(errCode === "CODE_STILL_VALID")
                {
                    const sig = signatures[0];
                    processExpirationDate(sig);
                    setSignature(sig);
                    setRestartCountdownTrigger(Date.now());
                    setProceedToSignature(true);
                    return;
                }
                if(errCode === "ALREADY_SIGNED" && signatures[0]?.signAt)
                {
                    const sig = signatures[0];
                    processExpirationDate(sig);
                    setSignature(sig);
                    setRestartCountdownTrigger(Date.now());
                    setProceedToSignature(true);
                    return;
                }
            }
            toggleShowAlert();
        });
    };

    const submit = (signature: ISignature) =>
    {
        showModal();
        var data: any = {id: refund.id, signatureId: state.signature.id};
        if(isOutdated && outdatedPlanId != null)
        {
            data.outdatedPlanId = outdatedPlanId;
            data.endDate = endDate;
            data.daysExpired = daysExpired;
            if(typeof recharge === 'number')
            {
                data.recharge = recharge;
            }
            if(typeof refundAmount === 'number')
            {
                data.refundAmount = refundAmount;
            }
        }
        OrderService.patchOrder(data).then((response: OrderType) =>
        {
            if(response.signatureId)
            {
                history.push("/portfolio/orders");
            }
            else
            {
                hideModal();
                toggleShowAlert();
            }
        })
        .catch((error) =>
        {
            hideModal();
            toggleShowAlert();
        });
    };

    return(
        <React.Fragment>
            <DocumentTitle title='IronIA - Firma de Reembolso'>
                <React.Fragment>
                    <S.HelperStyle />
                    <Lay.CardHeadingContainer>
                        <h2>Firma de Reembolso</h2>
                    </Lay.CardHeadingContainer>
                    <Lay.SeparatorTop />
                    <S.Cont>
                        <S.MainCont>
                            {showAlert &&
                            (
                                <MessageBar messageBarType={MessageBarType.error} isMultiline={false} dismissButtonAriaLabel="Close">
                                    Se ha producido un error al procesar la operación.
                                </MessageBar>
                            )}
                            {isOutdated && !proceedToSignature &&
                            (
                                <React.Fragment>
                                    <S.OutdatedInfoText>
                                        Este reembolso corresponde a un Plan de Cartera caducado. Este plan de Cartera lleva caducado desde el día {endDate}, un total de {daysExpired} días, por lo que 
                                        {' '}
                                        se cobrará la deuda generada por la suscripción caducada que asciende a un total de {currencyNeutreFormatter.format(recharge)} €.
                                        {' '}
                                        <br />
                                        <br />
                                        <b>El cálculo del recargo se hace sobre el día de la orden de reembolso con los precios de mercado del día de hoy, por lo que el importe 
                                        final reembolsado podría ser diferente al importe calculado aquí.</b>
                                        <br />
                                        <br />
                                        {typeof refundAmount === 'number' && refundAmount > 0 && `El importe del reembolso sobre el que realizaremos los cálculos hoy es de ${currencyNeutreFormatter.format(refundAmount)} €.`}
                                        {typeof recharge === 'number' && typeof refundAmount === 'number' && refundAmount > 0 && recharge > 0 &&
                                        (
                                            <React.Fragment>
                                                {recharge > refundAmount
                                                    ? ` De ese importe de reembolso se sustraerán ${currencyNeutreFormatter.format(refundAmount)} € en concepto de pago de suscripción caducada. Deuda pendiente por abonar: ${currencyNeutreFormatter.format(recharge - refundAmount)} €.`
                                                    : ` De ese importe de reembolso se sustraerán ${currencyNeutreFormatter.format(recharge)} € en concepto de pago de suscripción caducada. Importe a recibir tras procesar la operación: ${currencyNeutreFormatter.format(refundAmount - recharge)} €.`}
                                                <br />
                                                <br />
                                            </React.Fragment>
                                        )}
                                        Al pulsar en el botón de "Tramitar Reembolso", se procederá a firmar la orden de reembolso y se procesará la operación. Si no deseas proceder, puedes volver atrás pulsando el botón "Cancelar" y renovar tu Plan de Cartera.
                                    </S.OutdatedInfoText>
                                    <S.OutdatedButtonCont>
                                        <Lay.CardButton size="lg" type="button" onClick={goBackToRefunds}>
                                            Cancelar
                                        </Lay.CardButton>
                                        <Lay.CardButton size="lg" type="button" onClick={handleProcessRefund} disabled={sendingNewCode}>
                                            <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                {sendingNewCode && <Spinner size={SpinnerSize.small} />}
                                                Tramitar Reembolso
                                            </span>
                                        </Lay.CardButton>
                                    </S.OutdatedButtonCont>
                                </React.Fragment>
                            )}
                            {(proceedToSignature || !isOutdated) &&
                            (
                                <SignatureComponent signatures={[signature]}
                                    key={restartCountdownTrigger > 0 ? restartCountdownTrigger : "initial-signature"}
                                    restartCountdownTrigger={restartCountdownTrigger}
                                    title={isOutdated ? '' : "Firma del Reembolso"}
                                    submitButtonText="Firmar Reembolso"
                                    userIdentities={[refund?.userIdentity]}
                                    alreadySignedText="¡Tu orden de reembolso está en proceso!"
                                    cancelButtonText="Volver"
                                    headerTextArea={`Introduzca la clave SMS que le hemos enviado a ${phoneNumber} para validar su reembolso.`}
                                    backButtonText="Volver"
                                    onBackButtonClicked={goBackToRefunds}
                                    onCancelButtonClicked={goBackToRefunds}
                                    requestNewCodeButtonText="Recibir nuevo código"
                                    onNewCodeSended={(signature: any) =>
                                    {
                                        processExpirationDate(signature);
                                        setSignature(signature);
                                    }}
                                    onSignatureChanged={(signature: any) =>
                                    {
                                        processExpirationDate(signature);
                                        setSignature(signature);
                                    }}
                                    onSigned={(signature: any) =>
                                    {
                                        processExpirationDate(signature);
                                        setSignature(signature);
                                        submit(signature);
                                    }}
                                />
                            )}
                            <ProcessingModalComponent isModalOpen={isModalOpen} hideModal={hideModal} />
                        </S.MainCont>
                    </S.Cont>
                </React.Fragment>
            </DocumentTitle>
        </React.Fragment>
    );
};

export default RefundsSignature;